"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Menu, X } from "lucide-react";

import GlowButton from "../ui/GlowButton";
import LanguageSwitcher from "./LanguageSwitcher";

// Shared brand asset for the navbar and the in-flow hero wordmark.
export const BRAND_LOGO = { src: "/logobig.webp", width: 1620, height: 242 };

// Section items underline when their section scrolls into view; route items
// (Blog) underline on exact path match. sectionId targets are matched against
// elements that later 2026 sections will render with the same id. Labels are
// resolved from the `nav` message namespace via `key`.
const NAV_ITEMS = [
  { key: "portfolio", href: "/portfolio", route: "/portfolio" },
  { key: "techStack", href: "/tech-stack", route: "/tech-stack" },
  { key: "aboutUs", href: "/about-us", route: "/about-us" },
  { key: "ourTeam", href: "/our-team", route: "/our-team" },
  { key: "blog", href: "/blog", route: "/blog" },
];

export default function Navbar2026() {
  const tNav = useTranslations("nav");
  const tCommon = useTranslations("common");
  const pathname = usePathname();
  const [activeSection, setActiveSection] = useState(null);
  const [open, setOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [heroGone, setHeroGone] = useState(false);
  const showLogo = pathname !== "/" || heroGone || open;

  useEffect(() => {
    const updateLogo = () => {
      setScrolled(window.scrollY > 24);
      const hero = document.querySelector("[data-hero-splash]");
      if (!hero) { setHeroGone(true); return; }
      const rect = hero.getBoundingClientRect();
      setHeroGone(rect.top + rect.height * (reducedMotion ? 1 : 0.65) <= 0);
    };
    updateLogo();
    window.addEventListener("scroll", updateLogo, { passive: true });
    window.addEventListener("resize", updateLogo);
    return () => {
      window.removeEventListener("scroll", updateLogo);
      window.removeEventListener("resize", updateLogo);
    };
  }, [pathname, reducedMotion]);

  // Track which in-page section is currently centered in the viewport.
  useEffect(() => {
    const els = NAV_ITEMS.filter((i) => i.sectionId)
      .map((i) => document.getElementById(i.sectionId))
      .filter(Boolean);
    if (!els.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -55% 0px" },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [pathname]);

  const isActive = (item) =>
    item.route ? pathname === item.route : activeSection === item.sectionId;

  return (
    <header className={`fixed inset-x-0 top-0 ${open ? "z-[70]" : "z-50"} transition-colors duration-300 motion-reduce:transition-none ${pathname === "/" && !scrolled && !open ? "bg-transparent" : "bg-black/95 backdrop-blur-md"}`}>
      <nav className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-6 md:px-10">
        <motion.div
          initial={false}
          animate={{ opacity: showLogo ? 1 : 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.4, ease: "easeOut" }}
          className="shrink-0"
          style={{ pointerEvents: showLogo ? "auto" : "none" }}
          aria-hidden={!showLogo}
        >
        <Link href="/" aria-label={tNav("homeAria")} tabIndex={showLogo ? 0 : -1}>
          <Image
            src={BRAND_LOGO.src}
            alt="Celestial Code"
            width={BRAND_LOGO.width}
            height={BRAND_LOGO.height}
            sizes="200px"
            priority
            className="h-[26px] w-auto"
          />
        </Link>
        </motion.div>

        {/* Desktop nav. The handoff waits for lg, not md: the Thai labels are
            wider than their English counterparts and at 768-1023px the row
            wrapped onto three lines, overlapped the wordmark and pushed the
            language switcher off-screen. English fits — measure in Thai. */}
        <div className="hidden items-center gap-9 lg:flex">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.key}
                href={item.href}
                className="relative py-1 text-base text-white/85 transition-colors hover:text-white"
              >
                {tNav(item.key)}
                {active && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute -bottom-0.5 left-0 right-0 h-[2px] rounded-full bg-white"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
              </Link>
            );
          })}

          <GlowButton href="/contact-us">{tCommon("cta.getInTouch")}</GlowButton>
          <LanguageSwitcher />
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          aria-label={open ? tNav("closeMenu") : tNav("openMenu")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="text-white lg:hidden"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="px-4 lg:hidden">
          <div className="flex flex-col gap-1 rounded-2xl border border-white/15 bg-black/80 p-4 backdrop-blur-xl">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.key}
                href={item.href}
                onClick={() => setOpen(false)}
                className={`rounded-lg px-3 py-3 text-lg transition-colors ${
                  isActive(item)
                    ? "text-white underline underline-offset-4"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {tNav(item.key)}
              </Link>
            ))}
            <GlowButton
              href="/contact-us"
              onClick={() => setOpen(false)}
              className="mt-2 w-full"
            >
              {tCommon("cta.getInTouch")}
            </GlowButton>
            <LanguageSwitcher className="mt-2 justify-center" />
          </div>
        </div>
      )}
    </header>
  );
}
